import { Link } from "react-router-dom";
import { IconNames } from "../../../../../enums/icons.enums";
import { CategoryProduct } from "../../../../../types/CategoryTypes.interface";
import Button from "../../../../Atoms/Button/Button";
import QuantityPicker from "@shared/components/Molecules/QuantityPicker/QuantityPicker.component";
import ImageWithFallback from "../../../../Molecules/ImageWithFallback/ImageWithFallback.component";
import { SvgIcon } from "@shared/components/Atoms/SvgIcon/SvgIcon";
import { useBasket } from "@shared/context/BasketContext";
import { useToast } from "@shared/context/ToastContext";
import { useTranslations } from "@shared/context/useTranslations";
import { ProductPriceResponse } from "@shared/helpers/apis/fetchProductPrices";
import { formatNumber } from "@shared/helpers/formatPrice";
import { getRoutePathByPageType } from "@shared/helpers/findPathForSystemSite";
import { StockStatus } from "@shared/components/Molecules/StockStatus/StockStatus.component";
import { useFavoriteManagement } from "@shared/hooks/useFavoriteManagement";
import { FavoritesModal } from "@shared/components/Molecules/FavoritesModal/FavoritesModal.component";
import S from "./Item.module.scss";
import { cn } from "@shared/helpers/classNames.helper";
import { useState, useEffect } from "react";
import { getThumbnailImageUrl } from "@shared/helpers/apis/productHelper";
import { CraneChildItems } from "@shared/components/Organisms/CraneChildItems/CraneChildItems.component";
import { useCrane } from "@shared/context/CraneContext";
import { Spinner } from "@shared/components/Atoms/Spinner/Spinner.component";
import RightArrowIcon from "@shared/assets/icons/rightarrow.svg?react";

interface ProductList_ItemProps {
    product: CategoryProduct;
    path?: string;
    prices?: ProductPriceResponse;
    pricesLoaded?: boolean;
    quantity: number;
    onQuantityChange: (quantity: number) => void;
}

export const ProductList_Item = ({
    product,
    prices,
    pricesLoaded,
    quantity,
    onQuantityChange,
}: ProductList_ItemProps) => {
    const { addToBasket } = useBasket();
    const { showToast } = useToast();
    // @ts-expect-error
    const currentCulture = globalThis?.currentCulture;
    const [showContactForPrice, setShowContactForPrice] = useState(false);
    const [pricesLoading, setPricesLoading] = useState(prices ? false : true);
    const fav = useFavoriteManagement(product);
    const {
        isProductFavorited,
        handleFavoriteClick,
        handleRemoveFromAllLists,
    } = fav;

    const basketPath = getRoutePathByPageType("Basket") || "/basket";
    const searchPath = getRoutePathByPageType("Search") || "/search";
    const { getTranslation } = useTranslations();
    const { isCraneMode, craneInfo } = useCrane();

    const lc = product?.lifeCycle;
    const isProductUnavailable = lc?.isUnavailable ?? false;
    const isDkOwned = product?.isDkOwned || product?.productOwner?.toUpperCase() === "DK";

    const handleAddToBasket = () => {
        addToBasket({
            productId: product.id.toString(),
            quantity,
            data: { url: product.images[0]?.url },
        });
        showToast(
            <>
                {quantity} {getTranslation("general.piece")} {product.name}{" "}
                {getTranslation("general.toastMessage.addedTo")}{" "}
                <Link to={basketPath}>{getTranslation("general.toastMessage.yourBasket")}</Link>
            </>
        );
    };

    const quantityDiscountTooltipContent = prices?.discountTable?.quantityDiscount
        ? Object.entries(prices?.discountTable?.quantityDiscount).map(
              ([key, value]: [string, any]) => (
                  <div key={key} className={S.discountPlacement}>
                      <span className={S.discountPlacementLeft}>
                          {getTranslation("general.quantity")} {value.quantity}{" "}
                          {getTranslation("general.pricePerPiece")}:{" "}
                      </span>
                      <span className={S.discountPlacementRight}>
                          {formatNumber(value.netPrice, currentCulture, { decimals: 2 })}{" "}
                      </span>
                  </div>
              )
          )
        : null;

    const showChildItems = new Boolean(isCraneMode && product?.children && product.children?.length > 0).valueOf();

    useEffect(() => {
        const missingPrice = !new Boolean(prices?.discountedPrice && prices?.discountedPrice > 0).valueOf();
        const isUnavailable = new Boolean(product?.isUnAvailable || false).valueOf();
        setShowContactForPrice(missingPrice || isUnavailable);
        setPricesLoading(pricesLoaded ? false : !prices);
    }, [prices, pricesLoaded, product]);

    return (
        <div className={cn(S.productItemWrapper, isProductUnavailable ? S.unavailable : "", isDkOwned && !isProductUnavailable ? S.dkOwned : "")}>
            <div className={cn(S.box, isCraneMode ? S.craneMode : "", showContactForPrice && S.showContactForPrice)}>
                <Link className={S.productImgWrapper} to={product?.url?.urlPath || ""} state={{ product: product?.id }}>
                    <ImageWithFallback
                        src={getThumbnailImageUrl(product.images[0]?.url)}
                        showFallback={product.images?.length === 0}
                        alt={product.name}
                        className={S.productImg}
                    />
                </Link>
                
                {!isProductUnavailable ? (
                    <div className={S.stockStatus}>
                        <div className={S.stockStatusBg}></div>
                        <StockStatus stock={product?.stock} />
                    </div>
                ) : null}
                
                <div className={S.productInfo}>
                    {!isProductUnavailable ? (
                        <Link to={product?.url?.urlPath || ""} state={{ product: product?.id }} className={S.productTextWrapper}>
                            <span className={S.productNumber}>{product.id}</span>
                            <span className={S.productName}>{product.name}</span>
                            <span className={S.productText}>{product?.shortDescription}</span>
                        </Link>
                    ) : (
                        <div className={S.productTextWrapper}>
                            <Link to={product?.url?.urlPath || ""} state={{ product: product?.id }}>
                                <span className={cn(S.productNumber, S.unavailableNumber)}>{product.id}</span>
                                <span className={cn(S.productName, S.unavailableName)}>{product.name}</span>
                            </Link>
                            <div className={S.unavailableInfo}>
                                <span className={cn(S.productText, S.unavailableText)}>{product?.shortDescription}</span>
                                <div className={S.unavailableBadges}>
                                    <span className={S.unavailableTag}>{getTranslation("product.unavailable")}</span>
                                    {lc?.replacedByProductNumber && (
                                        <Link
                                            className={S.replacedByLink}
                                            to={lc.replacedByProductUrl || `${searchPath}?q=${encodeURIComponent(lc?.replacedByProductNumber ?? '')}`}
                                        >
                                            <span className={S.replacedByLinkText}>{getTranslation("product.replacedBy")} {lc.replacedByProductNumber}</span>
                                            <RightArrowIcon className={S.rightArrowIcon} />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showChildItems && <div className={cn(S.productChildren, S.desktopOnly)}><CraneChildItems items={product.children || []} /></div>}
                </div>

                {isProductUnavailable ? null : pricesLoading ? (
                    <Spinner className={S.pricesSpinner} />
                ) : isProductUnavailable ? null : showContactForPrice ? (
                    <div className={S.afterSales}>
                        {getTranslation("crane.contactAfterSales")}
                    </div>
                ) : prices?.discountedPrice && prices?.discountedPrice > 0 ? (
                    <div className={S.prices}>
                        <div className={S.productPrices}>
                            {prices.discountPerUnit > 0 ? (
                                <div>
                                    {prices?.listPrice && (
                                        <span className={S.oldPrice}>
                                            {formatNumber(prices?.listPrice, currentCulture, {
                                                decimals: 2,
                                            })}{" "}
                                            {prices?.discountTable?.currencyCode.toUpperCase()}
                                        </span>
                                    )}

                                    {prices?.discountPerUnit && (
                                        <span className={S.discount}>
                                            -
                                            {formatNumber(prices?.discountPerUnit, currentCulture, {
                                                decimals: 2,
                                            })}{" "}
                                            {prices?.discountTable?.currencyCode.toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            ) : null}

                            <span
                                className={cn(
                                    S.price,
                                    prices.discountPerUnit === 0 ? S.priceOnly : ""
                                )}
                            >
                                {formatNumber(prices?.discountedPrice, currentCulture, {
                                    decimals: 2,
                                })}{" "}
                                {prices?.discountTable?.currencyCode.toUpperCase()}
                            </span>
                        </div>
                    </div>
                ) : (
                    <span className={S.priceContact}>
                       {getTranslation("general.contactForPrice")}
                    </span>
                )}

                {!isProductUnavailable && <div className={S.btns}>
                    {(showContactForPrice || isDkOwned ? (
                            <a
                                className={S.afterSalesBtn}
                                href={`mailto:${getTranslation("crane.afterSales.email")}?subject=${encodeURIComponent(getTranslation("crane.afterSales.subject", { serial: craneInfo?.serialNumber || "", craneName: craneInfo?.craneName || "" }))}&body=${encodeURIComponent(getTranslation("crane.afterSales.body", { id: product.id || "", productName: product.name || "" }))}`}
                            >
                                <SvgIcon iconName={IconNames.Mail} />
                                {getTranslation("crane.afterSales.contact")}
                            </a>
                    ) : prices?.discountedPrice && prices?.discountedPrice > 0 ? (
                        <>
                            <div className={S.productCounter}>
                                <QuantityPicker quantity={quantity} setQuantity={onQuantityChange} />
                            </div>
                            <div className={S.productBtn}>
                                <Button
                                    className={S.addToBasketBtn}
                                    variant="primary"
                                    onClick={handleAddToBasket}
                                    iconName={IconNames.Basketleft}
                                >
                                    {getTranslation("button.buy")}
                                </Button>
                            </div>
                        </>
                    ) : null)}
                </div>}

                <div className={S.extraOptions}>
                    {prices?.discountTable?.quantityDiscount && (
                        <Button
                            tooltipContent={quantityDiscountTooltipContent}
                            tooltipId="listDiscount-tooltip"
                            showTip
                            tooltipPlace="top"
                            className={S.favIconBtn}
                            iconName={IconNames.Discount}
                        >
                            {" "}
                        </Button>
                    )}

                    {(!isProductUnavailable || isProductFavorited) && (
                        <Button
                            className={S.heartBtn}
                            onClick={isProductUnavailable ? handleRemoveFromAllLists : handleFavoriteClick}
                        >
                            {isProductFavorited ? (
                                <SvgIcon iconName={IconNames.Heartfull} />
                            ) : (
                                <SvgIcon iconName={IconNames.Heart} />
                            )}
                        </Button>
                    )}
                </div>

                {showChildItems && <div className={cn(S.productChildren, S.mobileOnly)}><CraneChildItems items={product.children || []} /></div>}
            </div>


            <FavoritesModal controller={fav} />
        </div>
    );
};

export default ProductList_Item;
